import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Lock, Mail } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { axios.get(`${API}/api/config`).then(r => setCfg(r.data)).catch(() => {}); }, []);

  const companyName = cfg?.company_name || "";
  const companyShort = cfg?.company_short || "AS";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenido, ${user.name}`);
      // Iter 205 (P0) — Si el usuario debe cambiar contraseña, navegar
      // DIRECTAMENTE a /cambiar-contrasena. Antes navegaba a /admin y dejaba
      // que ProtectedRoute redirigiera, lo que producía pantalla en blanco
      // por desincronización entre window.location.pathname y el estado del router.
      if (user.must_change_password) {
        navigate('/cambiar-contrasena', { replace: true });
      } else if (user.role === 'employee' || user.role === 'empleado') {
        navigate('/empleado', { replace: true });
      } else {
        navigate('/admin', { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sesion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-red-600 rounded-sm flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-lg font-['Barlow_Condensed']">{companyShort}</span>
          </div>
          <h1 className="heading-industrial text-3xl font-bold text-slate-900" data-testid="login-title">{companyName}</h1>
          <p className="text-sm text-slate-500 mt-1">Acceso al sistema de gestion</p>
        </div>

        <Card className="border-slate-200 rounded-sm shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="heading-industrial text-lg uppercase">Iniciar Sesion</CardTitle>
            <CardDescription>Introduce tus credenciales para acceder</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-wider font-semibold text-slate-600">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@correo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 rounded-sm h-10 border-slate-200 focus:ring-2 focus:ring-slate-900"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider font-semibold text-slate-600">Contrasena</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 rounded-sm h-10 border-slate-200 focus:ring-2 focus:ring-slate-900"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm text-xs uppercase tracking-wider font-semibold h-10"
                data-testid="login-submit-btn"
              >
                {loading ? "Accediendo..." : "Acceder"}
              </Button>
              <Link to="/forgot-password" className="block text-center mt-2">
                <span className="text-xs text-slate-500 hover:text-slate-700 underline" data-testid="forgot-password-link">He olvidado mi contrasena</span>
              </Link>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
